/**
 * Created by Jerker Nord on 2022-10-28.
 */
import {BenjiKIVSymbolStates, ISymbol, SimpleReelModelState} from "../../../../core/reel/ReelInterfaces";
import {SimpleReelPaddingData} from "../../../reels/BenjiKIVReelModel";
import {BenjiKIVStatics} from "../../../BenjiKIVStatics";
import {BenjiKIVSymbol} from "../../../reels/BenjiKIVSymbol";
import {Back, gsap, Linear, Power1} from "gsap";
import {SymbolUtils} from "../../../utils/SymbolUtils";
import {BenjiKIV} from "../../../BenjiKIV";
import {BenjiKIVScatterSymbol} from "../../../reels/BenjiKIVScatterSymbol";
import {BenjiKIVStackedSymbol} from "../../../reels/BenjiKIVStackedSymbol";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class BenjiKIVReelView extends PIXI.Container {

    private _reelId:number;
    private _isRestore:boolean;
    private _reelSymbols:ISymbol[] = [];
    private _reelSymbolsContainer:PIXI.Container;
    private _stackedSymbolsContainer:PIXI.Container;
    private _stopSymbolNames:string[];
    private _stopping:boolean = false;
    private _spinning:boolean = false;
    private _tryToStop:boolean;
    private _stackedSymbolSpinBuffer:string[] = [];
    private _masterSpinTimeline:gsap.core.Timeline;
    private _masterSpinTimelineFast:gsap.core.Timeline;
    private _stateChangeCallback:Function;
    private _darkOverlay:PIXI.Sprite;
    private _startTweenNormal:gsap.core.Tween;
    private _startTweenFast:gsap.core.Tween;
    private _spinTweenNormal:gsap.core.Tween;
    private _spinTweenFast:gsap.core.Tween;
    private _startSpinNormalTL:gsap.core.Timeline;
    private _startSpinFastTL:gsap.core.Timeline;
    private _spinTL:gsap.core.Timeline;
    private _spinTLFast:gsap.core.Timeline;
    private _stopSteps:number;
    private _addSymbol:ISymbol;
    private _allowedSymbolCounter:number = 0;
    private _activateDarkReelTL:gsap.core.Timeline;
    private _deActivateDarkReelTL:gsap.core.Timeline;

    public paddingConfig:SimpleReelPaddingData = {
        visualPaddingBefore : 1,
        paddingBefore : 2,
        visibleSymbols : 3,
        paddingAfter : 2,
        visualPaddingAfter : 1,
    }
    public animatedStepData = {
        y : 0
    }

    constructor(id:number, stateChangeCallback:Function) {
        super();
        this.name = "Reel " + id;
        this._reelId = id;
        this.position = BenjiKIVStatics.reelPositions[id];
        this._isRestore = false;
        this._stateChangeCallback = stateChangeCallback;
        this._reelSymbolsContainer = new PIXI.Container();
        this.addChild(this._reelSymbolsContainer);
        this._stackedSymbolsContainer = new PIXI.Container();
        this._stackedSymbolsContainer.visible = false;
        this.addChild(this._stackedSymbolsContainer);
        this._darkOverlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._darkOverlay.tint = 0x000000;
        this._darkOverlay.alpha = 0;
        this._darkOverlay.width = BenjiKIVStatics.symbolWidth;
        this._darkOverlay.height = BenjiKIVStatics.symbolHeight * 3;
        this._darkOverlay.position.y = BenjiKIVStatics.symbolHeight * 3;
        this._darkOverlay.position.x -= BenjiKIVStatics.symbolWidth / 2;
        this._darkOverlay.position.y -= BenjiKIVStatics.symbolHeight / 2;
        this.addChild(this._darkOverlay);

        this.initStartStepTween();
        this.initInitSpinTween();
        this.initSpinTL();
        this.initMasterTL();
        this.initStartSpinNormalTL();
        this.initStartSpinFastTL();
        this._activateDarkReelTL = this.createActivateDarkReelTL();
        this._deActivateDarkReelTL = this.createDeActivateDarkReelTL();
    }

    public init(symbols:string[]) {
        for(let i:number = 0; i < symbols.length; i++) {
            const symbol:ISymbol = SymbolUtils.createSymbol(false, symbols[i], BenjiKIVSymbolStates.NORMAL, {
                reelID : this._reelId,
                position : i
            })
            if((i == 4) && symbol.stackedSymbolName.indexOf("W") == -1 && symbol.stackedSymbolName.indexOf("_MID") > -1) {
                //We have a MID sym, full stacked, on restore
                if(BenjiKIV.model.initDeadPresidents[this._reelId] == true) {
                    if(BenjiKIV.model.isRestore == true) {
                        //It's dead restore
                        (<BenjiKIVStackedSymbol>symbol).getNewStateAnimation(BenjiKIVSymbolStates.M_DEAD, 0);
                    }
                }
            }
            this._reelSymbols.push(symbol);
            this._reelSymbolsContainer.addChild(<BenjiKIVSymbol>symbol);
        }

        this.updatePosition();
    }

    private createActivateDarkReelTL():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({paused : true});
        tl.add(gsap.to(this._darkOverlay, {duration : 0.2, alpha : 0.70}));
        return tl;
    }

    private createDeActivateDarkReelTL():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({paused : true});
        tl.add(gsap.to(this._darkOverlay, {duration : 0.2, alpha : 0}));
        return tl;
    }

    public activateDarkReel():void {
        this._activateDarkReelTL.restart();
    }

    public deActivateDarkReel():void {
        this._deActivateDarkReelTL.restart();
    }

    public doNewGameDeadPresidents():void {
        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            if((i == 4) && this._reelSymbols[i].stackedSymbolName.indexOf("W") == -1 && this._reelSymbols[i].stackedSymbolName.indexOf("_MID") > -1) {
                //It's dead, new game
                (<BenjiKIVStackedSymbol>this._reelSymbols[i]).getNewStateAnimation(BenjiKIVSymbolStates.M_DEAD, 1.5);
            }
        }
    }

    private initInitSpinTween():void {
        this._spinTweenNormal = gsap.to(this.animatedStepData, BenjiKIVStatics.spinStepDurationNormal, {
            y : BenjiKIVStatics.stepSize,
            ease : Linear.easeNone,
            onStart : () => this.onAnimationStart(),
            onUpdate : () => this.updatePosition(),
            onComplete : () => this.onAnimationComplete()
        })
        this._spinTweenFast = gsap.to(this.animatedStepData, BenjiKIVStatics.spinStepDurationFast, {
            y : BenjiKIVStatics.stepSize,
            ease : Linear.easeNone,
            onStart : () => this.onAnimationStart(),
            onUpdate : () => this.updatePosition(),
            onComplete : () => this.onAnimationComplete()
        })
    }

    private initStartSpinNormalTL():void {
        this._startSpinNormalTL = gsap.timeline({
            paused : true,
            onStart : () => {this._stateChangeCallback(SimpleReelModelState.SPIN)},
            onComplete : () => {
                this.startSpin();
            }
        });

        this._startSpinNormalTL.add([
            this._startTweenNormal.restart(),
            () => {
                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    if(this._reelSymbols[i].symbolName == "S") {
                        (<BenjiKIVScatterSymbol>this._reelSymbols[i]).getNewStateAnimation(BenjiKIVSymbolStates.SPIN, BenjiKIVStatics.BLUR_DURATION_SPIN_START_NORMAL, BenjiKIVStatics.BLUR_DELAY_SPIN_START_NORMAL);
                    } else {
                        (<BenjiKIVSymbol>this._reelSymbols[i]).getNewStateAnimation(BenjiKIVSymbolStates.SPIN, BenjiKIVStatics.BLUR_DURATION_SPIN_START_NORMAL, BenjiKIVStatics.BLUR_DELAY_SPIN_START_NORMAL);
                    }
                }
            }
        ]);
    }

    private initStartSpinFastTL():void {
        this._startSpinFastTL = gsap.timeline({
            paused : true,
            onStart : () => {this._stateChangeCallback(SimpleReelModelState.SPIN)},
            onComplete : () => {
                this.startSpin();
            }
        });

        this._startSpinFastTL.add([
            this._startTweenFast.restart(),
            () => {
                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    if(this._reelSymbols[i].symbolName == "S") {
                        (<BenjiKIVScatterSymbol>this._reelSymbols[i]).getNewStateAnimation(BenjiKIVSymbolStates.SPIN, BenjiKIVStatics.BLUR_DURATION_SPIN_START_FAST, BenjiKIVStatics.BLUR_DELAY_SPIN_START_FAST);
                    } else {
                        (<BenjiKIVSymbol>this._reelSymbols[i]).getNewStateAnimation(BenjiKIVSymbolStates.SPIN, BenjiKIVStatics.BLUR_DURATION_SPIN_START_FAST, BenjiKIVStatics.BLUR_DELAY_SPIN_START_FAST);
                    }
                }
            }
        ]);
    }

    private initMasterTL():void {
        this._masterSpinTimeline = gsap.timeline({paused : true});
        this._masterSpinTimeline.add(this._spinTL.restart());

        this._masterSpinTimelineFast = gsap.timeline({paused : true});
        this._masterSpinTimelineFast.add(this._spinTLFast.restart());
    }

    private initSpinTL():void {
        this._spinTL = gsap.timeline({
            paused : true,
            repeat : -1,
            onComplete : () => {
                if(this._spinning == false) {
                    this._spinTL.pause();
                }
            }
        })
        this._spinTL.add(this._spinTweenNormal);

        this._spinTLFast = gsap.timeline({
            paused : true,
            repeat : -1,
            onComplete : () => {
                if(this._spinning == false) {
                    this._spinTLFast.pause();
                }
            }
        })
        this._spinTLFast.add(this._spinTweenFast);
    }

    private initStartStepTween():void {
        this._startTweenNormal = gsap.to(this.animatedStepData, BenjiKIVStatics.spinStartDurationNormal, {
            y : BenjiKIVStatics.stepSize,
            ease : Back.easeIn.config(3.5),
            onStart : () => this.onAnimationStart(),
            onUpdate : () => this.updatePosition(),
            onComplete : () => this.onAnimationComplete(),
            paused : true
        });
        this._startTweenFast = gsap.to(this.animatedStepData, BenjiKIVStatics.spinStartDurationFast, {
            y : BenjiKIVStatics.stepSize,
            ease : Back.easeIn.config(2),
            onStart : () => this.onAnimationStart(),
            onUpdate : () => this.updatePosition(),
            onComplete : () => this.onAnimationComplete(),
            paused : true
        });
    }

    public start():gsap.core.Timeline {
        this._stopSymbolNames = [];
        return BenjiKIV.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._startSpinFastTL : this._startSpinNormalTL;
    }

    private startSpin() {
        this._spinning = true;
        BenjiKIV.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._masterSpinTimelineFast.restart() : this._masterSpinTimeline.restart();
    }

    public activateStackedReel():PIXI.Container {
        this._stackedSymbolsContainer.visible = true;
        while(this._reelSymbolsContainer.children.length > 0) {
            this._stackedSymbolsContainer.addChild(<BenjiKIVSymbol>this._reelSymbolsContainer.removeChildAt(0));
        }
        this._reelSymbolsContainer.visible = false;

        return this._stackedSymbolsContainer;
    }

    public activateNormalReel(nudgeSteps:number):void {
        while(this._stackedSymbolsContainer.children.length > 0) {
            this._reelSymbolsContainer.addChild(<BenjiKIVSymbol>this._stackedSymbolsContainer.removeChildAt(0));
        }

        let stackedMidPos:number;
        if(nudgeSteps > 0) {
            stackedMidPos = 4 - nudgeSteps;
        } else {
            stackedMidPos = 4 + Math.abs(nudgeSteps);
        }

        for(let i:number = 0; i < Math.abs(nudgeSteps); i++) {
            const symbol:ISymbol = SymbolUtils.createSymbol(this._stopping, this.getRandomSymbolInReelSet(false), BenjiKIVSymbolStates.NORMAL, {
                reelID : this._reelId,
                position : 0
            });
            if(stackedMidPos > 4) {
                //Remove from top and add to bottom
                let removeSymbol:BenjiKIVSymbol = <BenjiKIVSymbol>this._reelSymbols.shift();
                removeSymbol.reset();
                this._reelSymbolsContainer.removeChild(removeSymbol);
                this._reelSymbols.push(symbol);
                this._reelSymbolsContainer.addChild(<BenjiKIVSymbol>symbol);
            } else {
                //Remove from bottom and add to top
                let removeSymbol:BenjiKIVSymbol = <BenjiKIVSymbol>this._reelSymbols.pop();
                removeSymbol.reset();
                this._reelSymbolsContainer.removeChild(removeSymbol);
                this._reelSymbols.unshift(symbol);
                this._reelSymbolsContainer.addChild(<BenjiKIVSymbol>symbol);
            }
        }
        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            this._reelSymbols[i].reelPosition = i;
        }
        this.updatePosition()
        this._reelSymbolsContainer.visible = true;
        this._stackedSymbolsContainer.visible = false;
        this._stackedSymbolsContainer.position.y = 0;
    }

    public stop():void {
        this._tryToStop = true;
    }

    public doStop(stopSymbolNames:string[]):void {
        this._stopSymbolNames = stopSymbolNames;
        this._stopSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        this._spinning = false;
        this._tryToStop = false;
        this._stopping = true;
        this._stateChangeCallback(SimpleReelModelState.STOPPING);
    }

    private onAnimationStart() {

    }

    public addSymbol() {
        const newSymbolPosition = this._reelSymbols[0].reelPosition - 1;
        let symbolName:string;

        if(this._stackedSymbolSpinBuffer.length > 0) {
            //Get the rest of the stacked sym
            symbolName = this._stackedSymbolSpinBuffer.pop()!;
        } else if(this._stopping == true) {
            //Get next stop sym
            symbolName = this._stopSymbolNames.pop()!;
        } else {
            if(this._tryToStop == true) {
                //Waiting for all reels to finish stacked syms. Getting a random, no-stacked sym.
                symbolName = this.getRandomSymbolInReelSet(false);
                if(BenjiKIV.allReelView.reelModels[this._reelId].state == SimpleReelModelState.SPIN) {
                    this._stateChangeCallback(SimpleReelModelState.READY_TO_STOP);
                }
            } else {
                //Spinning. Get a random whatever sym.
                if(this._reelSymbols[0].symbolName.indexOf("M") > -1) {
                    symbolName = this.getRandomSymbolInReelSet(false);
                } else {
                    symbolName = this.getRandomSymbolInReelSet(true);
                    if(symbolName.indexOf("M") > -1 || symbolName.indexOf("W") > -1) {
                        this._stackedSymbolSpinBuffer.push(symbolName + "_TOP");
                        this._stackedSymbolSpinBuffer.push(symbolName + "_MID");
                        symbolName = symbolName + "_BOT";
                    }
                }
            }
        }

        this._addSymbol = SymbolUtils.createSymbol(this._stopping, symbolName, BenjiKIVSymbolStates.SPIN, {
            reelID : this._reelId,
            position : newSymbolPosition
        });

        this._reelSymbols.unshift(this._addSymbol);
        this._reelSymbolsContainer.addChild(<BenjiKIVSymbol>this._addSymbol);
    }

    private doStopBounce(bounceFactor:number = 0.07, durationFactor:number = 0.4):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({onComplete : () => this._stateChangeCallback(SimpleReelModelState.STOPPED)});
        const overshoot:gsap.core.Tween = gsap.to(this.animatedStepData, BenjiKIVStatics.stepDuration * durationFactor, {
            y : BenjiKIVStatics.stepSize * bounceFactor,
            ease : Power1.easeOut,
            onStart : () => {
                this._stateChangeCallback(SimpleReelModelState.BOUNCE);
                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    (<BenjiKIVSymbol>this._reelSymbols[i]).getNewStateAnimation(BenjiKIVSymbolStates.NORMAL, BenjiKIVStatics.BLUR_DELAY_SPIN_STOP, BenjiKIVStatics.BLUR_DURATION_SPIN_STOP);
                }
            },
            onUpdate : () => this.updatePosition()
        });

        const back:gsap.core.Tween = gsap.to(this.animatedStepData, BenjiKIVStatics.reelStopBounceDuration, {
            y : 0,
            ease : Power1.easeOut,
            onUpdate : () => this.updatePosition()
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    //All symbols on reel have moved one symbol in height
    private onAnimationComplete() {
        let symbol:BenjiKIVSymbol = <BenjiKIVSymbol>this._reelSymbols.pop();
        if(symbol.symbolName == "S") {
            (<BenjiKIVScatterSymbol>symbol).reset();
        } else {
            symbol.reset();
        }
        this._reelSymbolsContainer.removeChild(symbol);

        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            const symbol:ISymbol = this._reelSymbols[i];
            (<BenjiKIVSymbol>symbol).reelPosition += 1;
        }

        this.animatedStepData.y = 0;

        if(this._stopping == true) {
            this._stopSteps--;
            if(this._stopSteps == 0) {
                if(this._masterSpinTimeline.isActive()) {
                    this._masterSpinTimeline.pause();
                } else {
                    this._masterSpinTimelineFast.pause();
                }
                this._stopping = false;
                this._tryToStop = false;
                //Do stop bounce
                this.doStopBounce();
            }
        }

        this.addSymbol();

        this.updatePosition();
    }

    private updatePosition() {
        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            const basePos = BenjiKIVStatics.stepSize * this._reelSymbols[i].reelPosition;
            this._reelSymbols[i].position.y = basePos + this.animatedStepData.y;
        }
    }

    private getRandomSymbolInReelSet(getStacked:boolean):string {
        if(this._allowedSymbolCounter == 2) {
            getStacked = false;
        }
        if(getStacked == true) {
            let symbolName:string;
            let randomIndex:number;
            //No Wilds on first and last reel
            if(this._reelId > 0 && this._reelId < 4) {
                randomIndex = Math.floor(Math.random() * BenjiKIVStatics.SYMBOLS.concat(BenjiKIVStatics.STACKED_SYMBOLS).length);
                symbolName = BenjiKIVStatics.SYMBOLS.concat(BenjiKIVStatics.STACKED_SYMBOLS)[randomIndex];
            } else {
                randomIndex = Math.floor(Math.random() * BenjiKIVStatics.SYMBOLS.concat(BenjiKIVStatics.STACKED_SYMBOLS_NO_WILD).length);
                symbolName = BenjiKIVStatics.SYMBOLS.concat(BenjiKIVStatics.STACKED_SYMBOLS_NO_WILD)[randomIndex];
            }
            if(this._allowedSymbolCounter < 2 && (symbolName.indexOf("M") > -1 || symbolName == "W" || symbolName == "S")) {
                this._allowedSymbolCounter++;
            }
            return symbolName;
        } else {
            if(this._allowedSymbolCounter > 0) {
                this._allowedSymbolCounter--;
            }
            return BenjiKIVStatics.SYMBOLS[Math.floor(Math.random() * BenjiKIVStatics.SYMBOLS.length)];
        }
    }

    get symbols():ISymbol[] {
        return this._reelSymbols;
    }

    public removeSymFromSymArray():void{
        this._reelSymbols=[];
    }

    get reelSymbolsContainer():PIXI.Container{
        return this._reelSymbolsContainer;
    }
}